/*
 * *
 *   ~  * Copyright (c) by INFODATA-GROUP. All rights reserved.
 *   ~  * No part of this publication may be reproduced, distributed,
 *   ~  * or transmitted in any form or by any means, including photocopying, recording,
 *   ~  * or other electronic or mechanical methods, without the prior written permission of the publisher,
 *   ~  * except in the case of brief quotations embodied in critical
 *   ~  * reviews and certain other noncommercial uses permitted by copyright law.
 *   ~  * AUTHOR : bs
 *   ~  * DATE : 12/05/2025 15:11
 *
 */

import { inject, Injectable } from '@angular/core';
import { COMMONS_ENVIRONMENT, CommonsEnvironment } from '../interface/environment/commons-environment.interface';
import { NavigationService } from './navigation.service';
import { PlatformEnum } from '../enum/platform.enum';
import { Router } from '@angular/router';
import { PlatformPreference } from '../interface/platform-preference.interface';
import { CommonUtils } from '../tool/common-utils';
import { OsMobileEnum } from '../enum/os/os-mobile.enum';
import { OsDesktopEnum } from '../enum/os/os-desktop.enum';
import { OsDevice } from '../types/os-device.type';

const LOCAL_STORAGE_KEY = 'platform-preference';
const URL_QUERY_PARAM = 'platformPreference';

@Injectable({
  providedIn: 'root',
})
export class PlatformService {
  protected readonly router = inject(Router);
  protected readonly env: CommonsEnvironment = inject(COMMONS_ENVIRONMENT);
  protected readonly navigationService = inject(NavigationService);

  protected platformPreference: PlatformPreference | null =
    this.getFromLocalStorage();

  protected userAgent = navigator.userAgent || (window as any).opera || '';

  protected enable = false;

  public get isEnabled(): boolean {
    return this.enable;
  }

  public get isMobileDevice(): boolean {
    const regex = [
      /(Android)(.+)(Mobile)/i,
      /BlackBerry/i,
      /iPhone|iPod|iPad/i,
      /Opera Mini/i,
      /IEMobile/i,
    ];
    return regex.some((b) => this.userAgent.match(b));
  }
  public get isTabletDevice(): boolean {
    const regex =
      /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/;
    return regex.test(this.userAgent.toLowerCase());
  }
  public get isDesktopDevice(): boolean {
    return !this.isMobileDevice && !this.isTabletDevice;
  }

  public get mobileOS(): OsMobileEnum | undefined {
    if (!this.isMobileDevice) {
      return undefined;
    }

    if (/windows phone/i.test(this.userAgent)) {
      return OsMobileEnum.WindowsPhone;
    }

    if (/android/i.test(this.userAgent)) {
      return OsMobileEnum.Android;
    }

    if (/iPad|iPhone|iPod/.test(this.userAgent) && !(window as any).MSStream) {
      return OsMobileEnum.iOS;
    }

    return OsMobileEnum.Unknown;
  }
  public get desktopOS(): OsDesktopEnum | undefined {
    if (!this.isDesktopDevice) {
      return undefined;
    }

    if (this.userAgent.indexOf('Win') !== -1) {
      return OsDesktopEnum.Windows;
    }

    if (this.userAgent.indexOf('Mac') !== -1) {
      return OsDesktopEnum.MacOS;
    }

    if (this.userAgent.indexOf('X11') !== -1) {
      return OsDesktopEnum.Unix;
    }

    if (this.userAgent.indexOf('Linux') !== -1) {
      return OsDesktopEnum.Linux;
    }

    return OsDesktopEnum.Unknown;
  }
  public get deviceOS(): OsDevice | undefined {
    return this.mobileOS ?? this.desktopOS;
  }

  constructor() {
    this.bootstrap();
  }

  protected bootstrap() {
    this.init();
    if (!this.isEnabled) {
      return;
    }
    this.checkRouter();
    this.redirect();
  }

  protected getFromLocalStorage(): PlatformPreference | null {
    const data = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!data) {
      return null;
    }
    try {
      return JSON.parse(data) as PlatformPreference;
    } catch {
      return null;
    }
  }

  protected saveToLocalStorage(): void {
    if (this.platformPreference) {
      localStorage.setItem(
        LOCAL_STORAGE_KEY,
        JSON.stringify(this.platformPreference),
      );
    }
  }

  protected init() {
    const config = this.env.platform;
    if (!config) {
      return;
    }

    if (!config.currentPlatform) {
      return;
    }

    if (config.currentPlatform === 'desktop' && config.mobileUrl) {
      this.enable = true;
    }

    if (config.currentPlatform === 'mobile' && config.desktopUrl) {
      this.enable = true;
    }
  }

  protected checkRouter() {
    const platform = new URLSearchParams(window.location.search).get(
      URL_QUERY_PARAM,
    );

    if (platform === PlatformEnum.desktop || platform === PlatformEnum.mobile) {
      this.platformPreference = {
        platform: platform,
        timestamp: Date.now(),
      };

      this.saveToLocalStorage();
      this.router
        .navigate([], {
          queryParams: {
            [URL_QUERY_PARAM]: null,
          },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        })
        .then();
    }
  }

  public redirect(): void {
    if (!this.enable) {
      return;
    }

    // If something is saved here, user doesn't want to be redirected
    if (this.platformPreference) {
      return;
    }

    if (
      this.env.platform.currentPlatform === PlatformEnum.desktop &&
      this.isMobileDevice
    ) {
      this.navigationService.goTo(this.env.platform.mobileUrl);
      return;
    }
    if (
      this.env.platform.currentPlatform === PlatformEnum.mobile &&
      this.isDesktopDevice
    ) {
      this.navigationService.goTo(this.env.platform.desktopUrl);
    }
  }

  public navigateToDesktop(): void {
    if (!this.enable) {
      return;
    }
    this.clearLocalStorage();
    const url = CommonUtils.addUrlParam(
      this.env.platform.desktopUrl,
      URL_QUERY_PARAM,
      PlatformEnum.desktop,
    );
    this.navigationService.goTo(url);
  }

  public navigateToMobile(): void {
    if (!this.enable) {
      return;
    }
    this.clearLocalStorage();
    const url = CommonUtils.addUrlParam(
      this.env.platform.mobileUrl,
      URL_QUERY_PARAM,
      PlatformEnum.mobile,
    );
    this.navigationService.goTo(url);
  }

  protected clearLocalStorage(): void {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  }
}
